import React, { useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import PageHeader from "../components/PageHeader";
import { NavLink, useNavigate } from "react-router-dom";
import emptysection from "../assets/empty-section.svg";
import setting from "../assets/setting.svg";
import sortIcon from "../assets/draghandle.svg";
import undoIcon from "../assets/undo.svg";
import trash from "../assets/red-trash.svg";
import cross from "../assets/red-cross.svg";
import copy from "../assets/copy.svg";
import addicon from "../assets/plus-blue.svg";
import { StandardFieldList } from "../action/sectiondata";
import { Spinner } from "react-bootstrap";
import SwitchToggle from "../components/switch";
import { saveForm } from "../action/formaction";
import { useToast } from "../components/ToastProvider";

/* ---------------------------
   Draggable element (library)
----------------------------*/
function DraggableField({ field }) {
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: field.id,
    data: { ...field, source: "library" },
  });

  const style = {
    // transform: transform
    //   ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
    //   : undefined,
    boxShadow: isDragging
      ? "-19px 10px 13px #0000000D, -34px 18px 15px #00000003, -53px 28px 17px #00000000"
      : undefined,
    background: isDragging ? "#fff" : undefined,
    zIndex: isDragging ? 999 : undefined,

    transform: CSS.Translate.toString(transform),
    transition: isDragging ? "none" : undefined,
    willChange: "transform",
    opacity: 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      className="field-item"
      title={field.label}
      onDoubleClick={(e) => e.preventDefault()}
    >
      <span className="field-label">{field.label}</span>
      {field.icon && (
        <img src={field.icon} alt={field.label} className="draggable-icon" />
      )}

    </div>
  );
}

/* ---------------------------
   Sortable dropped field
----------------------------*/
function SortableField({ item, onClick, selected, onDelete, onLabelChange, errors }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    transition: {
      duration: 250,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.95 : 1,
    zIndex: isDragging ? 999 : "auto",
    // background: isDragging ? "#fff" : undefined,
    // boxShadow: isDragging
    //   ? "-19px 10px 13px #0000000D, -34px 18px 15px #00000003, -53px 28px 17px #00000000"
    //   : undefined,
  };

  return (
    <>
    <div
      ref={setNodeRef}
      style={style}
      lassName="field-name-wrapper"
      {...attributes}
      className={`form-field-wrapper ${selected ? "selected" : ""}`}
      onClick={() => onClick(item.id)}
    >
      <img
        ref={setActivatorNodeRef}
        {...listeners}
        src={sortIcon}
        className="field-handle"
        alt="move"
      />

      <div className="field-content">

          <div className="field-badge">
            <img src={item.icon || setting} alt="icon" />   {/* ✔ show original icon */}
            <span className="field-label">{item.label}</span>
          </div>

        <div className="form-content">
          <input
            type="text"
            placeholder="Enter Field label or title"
            className="input"
            value={item.labelText}
            onChange={(e) => {
              e.stopPropagation();
              onLabelChange(item.id, e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClick(item.id); // make it active
            }}
            onFocus={(e) => {
              e.stopPropagation();
              onClick(item.id);
            }}
          />


          <img src={trash} alt="delete" className="delete" onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }} />

        </div>
     

      </div>
    </div>
       {
    errors?.fields?.[item.id]?.label && (
      <p className="error-text">{errors.fields[item.id].label}</p>
    )
  }
  </>
  );
}

/* ---------------------------
   Droppable + sortable area
----------------------------*/
const DropPlaceholder = () => <div className="dnd-hover-placeholder" />;

function DroppableInnerList({
  fields,
  setSelectedId,
  selectedId,
  handleDeleteField,
  onLabelChange,
  errors,
  previewIndex,
  showPreview,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: "form-drop-area" });

  return (
    <div
      ref={setNodeRef}
      className={`drop-box ${isOver ? "over" : ""}`}
      style={{ minHeight: 140,padding:"0px" }}
    >
      {fields.length === 0 ? (
        <div className="empt-state">
          <img src={emptysection} alt="empty" />
          <h6 className="text-center">Drop Fields Here</h6>
        </div>
      ) : (
        <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          {fields.map((f, index) => (
            <React.Fragment key={f.id}>
              {showPreview && previewIndex === index && <DropPlaceholder />}
              <SortableField
                item={f}
                selected={selectedId === f.id}
                onClick={setSelectedId}
                onDelete={handleDeleteField}
                onLabelChange={onLabelChange}
                errors={errors}
              />
            </React.Fragment>
          ))}
          {showPreview && previewIndex === fields.length && <DropPlaceholder />}
        </SortableContext>
      )}
    </div>
  );
}

/* ---------------------------
   MAIN COMPONENT
----------------------------*/
export default function CreateNewForm() {
  const [formName, setFormName] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [tomails, setTomails] = useState("");
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [backup, setBackup] = useState({ fields: [] });
  const [standardFields, setStandardFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOverInfo, setDragOverInfo] = useState({ containerId: null, index: null });
  const { showToast } = useToast();
  const [errors, setErrors] = useState({
    formName: "",
    fields: {},
    tomails:""
  });

  const emailRegex =
  /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(\s*,\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;

  const validateEmails = (value) => {
    // optional field → no error if empty
    if (!value || !value.trim()) return "";
  
    if (!emailRegex.test(value)) {
      return "Enter valid email(s), separated by commas";
    }
  
    return "";
  };


const navigate = useNavigate();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  const fetchStandardFields = async () => {
    setLoading(true);
    const res = await StandardFieldList();

    if (res?.status === 1) {
      const normalized = res.data.list.map((item) => ({
        id: item._id,
        label: item.title,
        type: item.type,
        placeholder: item.placeholder ? "" : null,
        required: item.required,
        icon: item.icon
          ? `${process.env.REACT_APP_BUCKET_URL}${item.icon}`
          : setting,
      }));

      setStandardFields(normalized);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchStandardFields();
  }, []);

  const handleLabelChange = (id, value) => {
    setFields(prev =>
      prev.map(f =>
        f.id === id ? { ...f, labelText: value } : f
      )
    );
  };

  // Create new dropped field
  const makeDroppedField = (lib) => ({
    id: `${lib.id}-${Date.now()}`,
    type: lib.type,
    label: lib.label,
    labelText: "",
    placeholder: lib.placeholder ? "" : "",
    required: false,
    icon: lib.icon, 
    standard_field: lib.id,
    options:
      lib.type === "multi-select" || lib.type === "single-select"
        ? ["Option 1", "Option 2"]
        : [],
  });

  const handleDragStart = (event) => {
    setDragOverInfo({ containerId: null, index: null });
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) {
      setDragOverInfo({ containerId: null, index: null });
      return;
    }

    if (active.data.current?.source !== "library") return;

    if (over.id !== "form-drop-area" && !fields.some((f) => f.id === over.id)) {
      setDragOverInfo({ containerId: null, index: null });
      return;
    }

    let index = fields.length;
    if (over.id === "form-drop-area") {
      if (fields.length === 0) index = 0;
    } else {
      const overIndex = fields.findIndex((f) => f.id === over.id);
      if (overIndex !== -1) {
        let modifier = 0;
        if (active.rect.current?.translated && over.rect) {
          const activeCenterY = active.rect.current.translated.top + active.rect.current.translated.height / 2;
          const overCenterY = over.rect.top + over.rect.height / 2;
          if (activeCenterY > overCenterY) modifier = 1;
        }
        index = overIndex + modifier;
      }
    }

    setDragOverInfo({ containerId: "form-drop-area", index });
  };

  const handleDragCancel = () => {
    setDragOverInfo({ containerId: null, index: null });
  };

  /* ---------------------
     Drag End
  ----------------------*/
  const handleDragEnd = ({ active, over }) => {
    setDragOverInfo({ containerId: null, index: null });

    if (!over) return;

    // Add from library
    if (active.data.current?.source === "library") {
      const lib = active.data.current;
      const newField = makeDroppedField(lib);

      setBackup({ fields: [...fields] });
      const insertIndex =
        dragOverInfo.containerId === "form-drop-area" && dragOverInfo.index !== null
          ? dragOverInfo.index
          : over.id === "form-drop-area"
            ? fields.length
            : fields.findIndex((f) => f.id === over.id);

      setFields((prev) => {
        const copy = [...prev];
        const safeIndex = insertIndex === -1 ? copy.length : insertIndex;
        if (safeIndex >= copy.length) copy.push(newField);
        else copy.splice(safeIndex, 0, newField);
        return copy;
      });
      setSelectedId(newField.id);
      return;
    }

    // Reorder
    const ids = fields.map((f) => f.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = over.id === "form-drop-area" ? oldIndex : ids.indexOf(over.id);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      setBackup({ fields: [...fields] });
      setFields((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  };

  /* ---------------------
     Delete
  ----------------------*/
  const handleDeleteField = (id) => {
    setBackup({ fields: [...fields] });
    setFields((prev) => prev.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  /* ---------------------
     Save Payload
  ----------------------*/
  const buildPayload = () => ({
    name: formName,
    shortcode: shortCode,
    extras: {
      to_mails:tomails
    },  
    fields: fields.map((f) => ({
      label: f.labelText || f.label,
      type: f.type,
      required: f.required,
      placeholder: f.placeholder,
      standard_field: f.standard_field, 
      ...(f.type === "multi-select" || f.type === "single-select"
        ? { options: f.options }
        : {}),
    })),
  });

  const validateForm = () => {
    let newErrors = { formName: "", fields: {} };
    let isValid = true;


    if (!formName.trim()) {
      newErrors.formName = "Form Name is required";
      isValid = false;
    }

    if (fields.length === 0) {
      showToast("Add at least one field", "error"); 
      return false;
    }

    // Validate each field
    fields.forEach((f) => {
      newErrors.fields[f.id] = {};

      // Label
      if (!f.labelText || !f.labelText.trim()) {
        newErrors.fields[f.id].label = "Enter field label/title";
        isValid = false;
      }

      // Placeholder
      if (f.placeholder !== null && !f.placeholder.trim()) {
        newErrors.fields[f.id].placeholder = "Placeholder is required";
        isValid = false;
      }

      // Multi-Select options
      if ((f.type === "multi-select" || f.type === "single-select") && f.options) {
        f.options.forEach((opt, i) => {
          if (!opt.trim()) {
            if (!newErrors.fields[f.id].options)
              newErrors.fields[f.id].options = {};

            newErrors.fields[f.id].options[i] = "Option cannot be empty";
            isValid = false;
          }
        });
      }
    });

    setErrors(newErrors);
    return isValid;
  };


  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = buildPayload();
    const res = await saveForm(payload);

    if (res?.status === 1) {
      showToast("Form created successfully", "success");
      navigate("/all-forms"); // or your redirect path
    } else {
      showToast(res?.message || "Something went wrong", "error");
    }
  };

  return (
    <div>
      <PageHeader pageTitle="Add New Form">
   
          <NavLink to="/all-forms" className="btn-white">Discard</NavLink>
          <button
            className="btn-black"
            onClick={handleSave}
          >
            Create
          </button>

     
      </PageHeader>

      <div className="page-container">
        <div className="row">

          {/* LEFT SIDE (Inside One DndContext) */}
          <div className="col-md-8">
            <DndContext
              sensors={sensors}
              collisionDetection={pointerWithin}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >

              {/* 1) STANDARD FIELDS CARD */}
              <div className="theme-card p-3 mb-3">
                <div className="card-title mb-3">Standard Fields</div>
                {loading ? (
                  <div className="center-div">
                    <Spinner size="md" />
                  </div>

                ) : (
                  <div className="draggable-fields add-new-form">
                    {standardFields.map((sf) => (
                      <DraggableField key={sf._id} field={sf} />
                    ))}
                  </div>
                )}
              </div>

              {/* 2) DROPPABLE AREA CARD */}
              <div className="theme-card p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="card-title">Form Fields</div>

                  {fields.length > 0 && (
                    <button
                      className="undo-btn"
                      onClick={() => setFields(backup.fields)}
                    >
                      <img src={undoIcon} alt="undo" /> Undo
                    </button>
                  )}
                </div>

                <DroppableInnerList
                  fields={fields}
                  selectedId={selectedId}
                  setSelectedId={setSelectedId}
                  handleDeleteField={handleDeleteField}
                  onLabelChange={handleLabelChange}
                  errors={errors}
                  previewIndex={dragOverInfo.containerId === "form-drop-area" ? dragOverInfo.index : null}
                  showPreview={dragOverInfo.containerId === "form-drop-area"}
                />

              </div>
            </DndContext>
          </div>

          {/* RIGHT SIDE EDITOR */}
          <div className="col-md-4">
            <div className="theme-card p-4 mb-3">
              <div className="card-title pb-3">Form Name</div>

              <input
                type="text"
                className="frm-input"
                placeholder="Enter Form Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
              {errors.formName && (
                <p className="error-text">{errors.formName}</p>
              )}
              <div className="input-with-icon right mt-3">
                <input
                  type="text"
                  className="frm-input"
                  placeholder="Short Code"
                  value={shortCode}
                  onChange={(e) => setShortCode(e.target.value)}
                />
                <img src={copy} className="input-icon" alt="copy" />
              </div>
            </div>

            <div className="theme-card p-4 mb-3">
              <div className="card-title pb-3">To Mails</div>

              <input
                type="text"
                className="frm-input"
                placeholder="Enter Mail Ids to send mail"
                value={tomails}
                onChange={(e) => {
                  const value = e.target.value;
                  setTomails(value);
                  setErrors((prev) => ({
                    ...prev,
                    tomails: validateEmails(value),
                  }));
                }}
              />

              {errors.tomails && (
                <p className="error-text">{errors.tomails}</p>
              )}
            </div>

            {/* Field settings */}
            <div className="theme-card p-4 mb-3">
              <div className="card-title pb-3">Field Settings</div>

              {!selectedId ? (
                <div className="text-muted">Select a field to edit</div>
              ) : (
                (() => {
                  const active = fields.find((f) => f.id === selectedId);
                  if (!active) return null;

                  return (
                    <>
                    <div className="mb-2" >
                        <label className="frm-label">Placeholder</label>
                        <input
                          type="text"
                          className="frm-input"
                          placeholder="Enter Placeholder"
                          value={active.placeholder}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f) =>
                                f.id === active.id
                                  ? { ...f, placeholder: e.target.value }
                                  : f
                              )
                            )
                          }
                        />
                        {errors?.fields?.[active.id]?.placeholder && (
                          <p className="error-text">{errors.fields[active.id].placeholder}</p>
                        )}

                    </div>
                      <div className="switch-btn field-setting">
                        Mark Field as Required
                        <SwitchToggle
                          checked={active.required}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f) =>
                                f.id === active.id ? { ...f, required: e.target.checked } : f
                              )
                            )
                          }
                        />

                      </div>
                      {(active.type === "multi-select" || active.type === "single-select") && (
                        <div className="multi-options">

                          {/* Existing options */}
                          {active.options.map((opt, i) => (
                            <div className="input-with-icon right mt-3" key={i}>
                              <label className="frm-label" >{`Enter option ${i + 1}`}</label>
                              <div className="input-option">
                                <input
                                  type="text"
                                  className="frm-input"
                                  placeholder={`Option ${i + 1}`}
                                  value={opt}
                                  onChange={(e) =>
                                    setFields((prev) =>
                                      prev.map((f) =>
                                        f.id === active.id
                                          ? {
                                            ...f,
                                            options: f.options.map((o, idx) =>
                                              idx === i ? e.target.value : o
                                            ),
                                          }
                                          : f
                                      )
                                    )
                                  }
                                />
                                <img
                                  src={cross}
                                  className="input-icon"
                                  alt="delete"
                                  onClick={() =>
                                    setFields((prev) =>
                                      prev.map((f) =>
                                        f.id === active.id
                                          ? {
                                            ...f,
                                            options: f.options.filter((_, idx) => idx !== i),
                                          }
                                          : f
                                      )
                                    )
                                  }
                                />
                              </div>
                              {errors?.fields?.[active.id]?.options?.[i] && (
                                <p className="error-text">{errors.fields[active.id].options[i]}</p>
                              )}

                            </div>
                          ))}

                          {/* Add New Option */}
                          <div
                            className="add_new_wrapper mt-3"
                            onClick={() =>
                              setFields((prev) =>
                                prev.map((f) =>
                                  f.id === active.id
                                    ? { ...f, options: [...f.options, `Option ${f.options.length + 1}`] }
                                    : f
                                )
                              )
                            }
                          >
                            <img src={addicon} alt="icon" />
                            Add New
                          </div>
                        </div>
                      )}

                    </>
                  );
                })()
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
